import { words } from './dictionary'

/** Crypto safe random integer generator for the browser */
function browserRandomInt(max: number) {
  if (typeof window === 'undefined') {
    throw new Error('browserRandomInt is not available in Node environments')
  }

  const cryptoRand = () => {
    const array = new Int8Array(4)
    window.crypto.getRandomValues(array)
    const dataView = new DataView(array.buffer)

    const uint = dataView.getUint32(0)
    return uint / (0xffffffff + 1) // 0xFFFFFFFF = uint32.MaxValue (+1 because Math.random is inclusive of 0, but not 1)
  }

  return Math.floor(cryptoRand() * (max + 1))
}

/** Environment specific random integer generator */
const randomInt: (v: number) => number =
  typeof window === 'undefined'
    ? (await import('node:crypto')).randomInt
    : browserRandomInt

/** Generate a random slug for use in URLs */
export function randomSlug(
  opts: {
    /** Number of words to generate */
    words?: number
    /** Length of the random salt to append to the end of the slug */
    salt?: number
    /** Separator to use between words */
    separator?: string
  } = {},
) {
  if (opts.words && (opts.words < 1 || opts.words > 4)) {
    throw new TypeError('Word count must be between 1-4')
  }

  opts.separator = opts.separator || '-'
  opts.words = opts.words || 2
  const salt = opts.salt ? [createSalt(opts.salt)] : []

  return getWords(opts.words).concat(salt).join(opts.separator)
}

const mergedWords = [...words.adjectives, ...words.nouns, ...words.verbs]

/** Generates the required number of words for the slug */
function getWords(count: number) {
  if (count === 0) return []

  if (count === 1) return [randomFromArray(mergedWords)]

  if (count === 2)
    return [randomFromArray(words.adjectives), randomFromArray(words.nouns)]

  if (count === 3)
    return [
      randomFromArray(words.adjectives),
      randomFromArray(words.nouns),
      randomFromArray(words.verbs),
    ]

  return [
    randomFromArray(words.adjectives),
    randomFromArray(words.adjectives),
    randomFromArray(words.nouns),
    randomFromArray(words.verbs),
  ]
}

/** Get a random value from an array */
export function randomFromArray(arr: any[]): any {
  return arr[randomInt(arr.length - 1)]
}

/** Generate a random salt of a given length */
export function createSalt(length: number): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'

  let value = ''
  for (let i = 0; i < length; i++) {
    value += chars[randomInt(chars.length - 1)]
  }

  return value
}

/** Get a random word from the dictionary with an optional type */
export function getRandomWord(type?: string) {
  if (type === 'adjective') return randomFromArray(words.adjectives)
  if (type === 'noun') return randomFromArray(words.nouns)
  if (type === 'verb') return randomFromArray(words.verbs)

  return randomFromArray(words.nouns)
}
